import { RefCallback } from 'preact'
import { useCallback, useEffect, useRef } from 'preact/hooks'

export interface Comments {
  before?: string[]
  after?: string[]
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

function renderComments(target: Element, { before = [], after = [] }: Comments): () => void {
  if (!target) {
    return noop
  }

  const beforeElements = before.map(c => document.createComment(c))
  const afterElements = after.map(c => document.createComment(c))

  beforeElements.forEach(el => target.parentNode.insertBefore(el, target))
  afterElements.forEach(el => target.parentNode.insertBefore(el, target.nextSibling))

  return () => {
    ;[...beforeElements, ...afterElements].forEach(el => el.remove())
  }
}

export interface UseRenderComments {
  ref: RefCallback<HTMLElement>
}

export const useRenderComments = (comments: Comments): UseRenderComments => {
  const cleanupFn = useRef(noop)
  const savedComments = useRef<Comments>(comments)

  useEffect(() => {
    savedComments.current = comments
  }, [comments])

  const setRef = useCallback((el: HTMLElement | null): void => {
    cleanupFn.current()
    if (el) {
      cleanupFn.current = renderComments(el, savedComments.current)
    }
  }, [])

  return { ref: setRef }
}
